<template>
    <div>
        <AdminBlock
            class="driver-droppable mb-0"
            :slim="true"
            :title="showTitle ? driver.name + (driver.has_left ? ' (left)' : '') : ''"
        >
            <template #options v-if="showTitle">
                <span class="badge bg-secondary" v-if="showCounts">
                    <!-- What is the count for? -->
                </span>
            </template>

            <ul
                ref="dropzoneRef"
                :class="[
                    'droppable list-unstyled rounded m-0',
                    model?.length > 0 ? 'dropzone-style p-2' : 'bg-gray-light border border-secondary-subtle p-3',
                    compactGrid ? 'dropzone-grid p-2' : '',
                    isDragOver ? 'drag-over' : ''
                ]"
                :data-driver-id="driver.id"
                v-bind="dropzoneProps"
            >
                <FloatingMovement
                    v-for="(movement, index) in model"
                    :key="movement.id"
                    v-model="model[index]"
                    :index="index"
                    :dropzone-id="dropzoneId"
                    :lastItem="index === model.length - 1"
                    :is-drag-over-index="!sortAlpha && dragOverIndex === index"
                    @dragover-index="handleDragOverIndex"
                    @drop-at-index="handleDropAtIndex"
                    @movement-moved-off-date="removeMovementFromDropzone"
                />

                <!-- Drop indicator at the end of the list -->
                <li
                    v-if="(state.isDragging || isDragOver) && model?.length > 0"
                    class="drop-indicator-end"
                    :class="{ 'active': dragOverIndex === model.length }"
                    @dragover.prevent="handleDragOverIndex(model.length)"
                    @drop.prevent="handleDropAtIndex($event, model.length)"
                ></li>

                <!-- Empty state message (only shows when no movements) -->
                <li
                    v-if="model?.length === 0"
                    class="text-center no-movements-text"
                    style="list-style: none;"
                    @dragover.prevent="handleDragOverIndex(0)"
                    @drop.prevent="handleDropAtIndex($event, 0)"
                >
                    <p class="mb-0">
                        {{ (state.isDragging || isDragOver) ? 'Drop here' : 'This Driver has no assigned Movements for this date' }}
                    </p>
                </li>
            </ul>
        </AdminBlock>
    </div>
</template>

<style>
.driver-droppable > .block-content {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;
}

.driver-droppable .droppable {
    min-height: 250px;
    border-style: dashed !important;
    border-width: 4px !important;
    transition: background-color 0.15s ease;
}

.driver-droppable .droppable.dropzone-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: start;
    gap: 0.75rem;
    align-items: flex-start;
    align-content: flex-start;
}

.driver-droppable .droppable.dropzone-grid > .floating-movement {
    flex: 0 1 31.25rem;
    width: 100%;
    max-width: 31.25rem;
    margin-bottom: 0 !important;
}

.driver-droppable .droppable.dropzone-grid > .drop-indicator-end,
.driver-droppable .droppable.dropzone-grid > .no-movements-text {
    flex: 0 0 100%;
    width: 100%;
}

.driver-droppable .droppable.drag-over {
    background-color: rgba(101, 163, 13, 0.1) !important;
    border-color: rgba(101, 163, 13, 0.5) !important;
}

/* Drop indicator styles */
.drop-indicator-end {
    height: 4px;
    background: transparent;
    margin: 4px 0;
    border-radius: 2px;
    transition: all 0.15s ease;
    list-style: none;
}

.drop-indicator-end.active {
    background: rgba(101, 163, 13, 0.8);
    height: 8px;
}

/* Ghost element styles (injected by drag start) */
:global(.drag-ghost-element) {
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    border-radius: 50px;
}
</style>

<script setup>
import {computed, ref, onUnmounted, onMounted, watch, inject} from 'vue';
import { useDropzone } from '@/composables/useDragAndDrop';
import AdminBlock from "@/components/movements/components/AdminBlock.vue";
import FloatingMovement from "@/components/movements/jobs/planner/FloatingMovement.vue";
import axios from "axios";
import { errorAlert, successAlert } from "@//utils";

const model = defineModel({
    type: Array,
    required: true,
    default: []
});

const props = defineProps({
    driver: {
        type: Object,
        required: true
    },
    showTitle: {
        type: Boolean,
        default: true
    },
    date: {
        type: String,
        required: true
    },
    showCounts: {
        type: Boolean,
        default: false
    },
    compactGrid: {
        type: Boolean,
        default: false,
    },
    sortAlpha: {
        type: Boolean,
        default: false
    }
});

const dropzoneRef = ref(null);

const movementCount = computed(() => model.value?.length ?? 0);

const dropzoneId = computed(() => `driver-${props.driver.id}`);

const markLocalMovementMutation = inject('markLocalMovementMutation', () => {});

/**
 * Sorts the model alphabetically by collection_locale (and delivery_locale as a fallback) if sortAlpha is enabled
 */
const sortModelAlpha = () => {
    if (props.sortAlpha && Array.isArray(model.value)) {
        model.value.sort(compareMovementsAlpha);
    }
};

const displayMovements = computed({
    get() {
        return Array.isArray(model.value) ? model.value : [];
    },
    set(value) {
        model.value = value;
    }
});


/**
 * Compare function for alphabetical sorting: first by collection_locale, then by delivery_locale
 */
const compareMovementsAlpha = (a, b) => {
    const aFrom = a.collection_locale || '';
    const bFrom = b.collection_locale || '';
    const fromCompare = aFrom.localeCompare(bFrom);

    if (fromCompare !== 0) {
        return fromCompare;
    }

    const aTo = a.delivery_locale || '';
    const bTo = b.delivery_locale || '';
    return aTo.localeCompare(bTo);
};

onMounted(() => {
    sortModelAlpha();
});

watch(() => model.value, () => {
    sortModelAlpha();
}, { deep: false });

const {
    dropzoneProps,
    isDragOver,
    dragOverIndex,
    setDragOverIndex,
    onDrop,
    cleanup,
    state,
} = useDropzone(
    dropzoneId,
    displayMovements,
    (newItems) => {
        if (props.sortAlpha) {
            newItems.sort(compareMovementsAlpha);
        }
        model.value = newItems;
        displayMovements.value = newItems;
    },
    onReorder,
    {
        allowSameListReorder: Number(props.driver.id) !== 0,
    }
);

/**
 * Triggered whenever any change is made to the list (add, remove, reorder)
 * Recalculates planned_position for all movements and persists to backend
 * Reassigns Driver when moving to a different Driver
 */
function onReorder(movements) {
    const movementIds = (movements || [])
        .map((movement) => Number(movement?.id))
        .filter((id) => Number.isFinite(id));

    // Reorders are optimistic in the UI; tag them as local to ignore self-echo highlights.
    if (movementIds.length > 0) {
        markLocalMovementMutation(movementIds, 3000);
    }

    const payload = {
        movements: movements || [],
        driver_id: props.driver.id,
    };

    if (payload.movements.length === 0) {
        return;
    }

    axios.post(route("api.movements.jobs.planner.reorder-movements"), payload)
        .then((response) => {
            successAlert(`Movement positions updated for ${props.driver.name}`, "Success");

            if (response.data.movements) {
                model.value.forEach((movement) => {
                    const updated = response.data.movements[movement.id];
                    if (updated) {
                        movement.status = updated.status;
                    }
                });
            }
        })
        .catch((errors) => {
            errorAlert(errors, `Error updating movement positions for driver ${props.driver.id}`);
        });
}

function handleDragOverIndex(index) {
    setDragOverIndex(index);
}

function handleDropAtIndex(event, index) {
    event?.stopPropagation?.();
    onDrop(event, index);
}

function removeMovementFromDropzone(movementId) {
    model.value = (model.value || []).filter(m => Number(m.id) !== Number(movementId));
}

onUnmounted(() => {
    cleanup();
});
</script>
